import { Mark } from '@tiptap/core'
import type { Editor } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import { findTimestamps, parseTimestampToSeconds } from '../timestamps'
import type { TimestampMatch } from '../timestamps'

/** Maximum document content size (in ProseMirror content units) for timestamp scanning. */
const MAX_TIMESTAMP_SCAN_SIZE = 10_000

export type TimestampAttributes = {
  timestamp: string
  startSeconds: number
  endSeconds: number | null
}

export type TimestampOptions = {
  onTimestampClick?: (timestamp: string) => void
  getTrackDurationSeconds?: () => number | undefined
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    timestamp: {
      setTimestamp: (attrs: TimestampAttributes) => ReturnType
    }
  }
}

export const TimestampMark = Mark.create<TimestampOptions>({
  name: 'timestamp',

  inclusive: false,

  addOptions() {
    return {
      onTimestampClick: undefined,
      getTrackDurationSeconds: undefined,
    }
  },

  addAttributes() {
    return {
      timestamp: { default: null },
      startSeconds: { default: null },
      endSeconds: { default: null },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-timestamp]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        'data-timestamp': HTMLAttributes.timestamp,
        'data-start-seconds': HTMLAttributes.startSeconds,
        'data-end-seconds': HTMLAttributes.endSeconds,
        class: 'rte-timestamp',
      },
      0,
    ]
  },

  addProseMirrorPlugins() {
    return [
      createTimestampPlugin(
        this.options.onTimestampClick,
        this.options.getTrackDurationSeconds,
        this.editor.isEditable,
      ),
    ]
  },
})

const timestampPluginKey = new PluginKey('timestampDecoration')

export function applyTimestampMarks(editor: Editor) {
  const { doc, schema } = editor.state
  const markType = schema.marks.timestamp
  if (!markType) {
    return
  }

  // Guard against running on unexpectedly large documents
  if (doc.content.size > MAX_TIMESTAMP_SCAN_SIZE) {
    console.warn(
      `[rte] applyTimestampMarks skipped: document size (${doc.content.size}) exceeds limit (${MAX_TIMESTAMP_SCAN_SIZE})`,
    )
    return
  }

  // Use a single transaction to remove then re-add timestamp marks atomically
  const tr = editor.state.tr

  // Step 1: Remove all existing timestamp marks so text nodes merge
  doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (!node.isText) {
      return
    }
    if (markType.isInSet(node.marks)) {
      tr.removeMark(pos, pos + node.nodeSize, markType)
    }
  })

  // Step 2: Re-scan the resulting document and add marks.
  // After removeMark, the doc on the transaction reflects the updated state.
  tr.doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (!node.isText) {
      return
    }

    const text = node.text as string
    for (const tc of findTimestamps(text)) {
      const from = pos + tc.index
      const to = from + tc.fullMatch.length
      const startSeconds = parseTimestampToSeconds(tc.start)
      const endSeconds = tc.end ? parseTimestampToSeconds(tc.end) : null

      tr.addMark(
        from,
        to,
        markType.create({
          timestamp: tc.fullMatch,
          startSeconds,
          endSeconds,
        }),
      )
    }
  })

  if (tr.steps.length > 0) {
    editor.view.dispatch(tr)
  }
}

export { timestampPluginKey }

function decorationClass(
  start: string,
  getTrackDurationSeconds?: () => number | undefined,
): string {
  const duration = getTrackDurationSeconds?.()
  if (duration === undefined) return 'rte-timestamp-decoration'
  const startSeconds = parseTimestampToSeconds(start)
  return startSeconds > duration
    ? 'rte-timestamp-decoration rte-timestamp-invalid'
    : 'rte-timestamp-decoration'
}

function getDecorationAttrs(
  tc: TimestampMatch,
  getTrackDurationSeconds: (() => number | undefined) | undefined,
  isEditable: boolean,
): Record<string, string> {
  const className = decorationClass(tc.start, getTrackDurationSeconds)
  const attrs: Record<string, string> = {
    class: className,
    'data-timestamp': tc.fullMatch,
    nodeName: 'span',
  }

  if (!isEditable && !className.includes('rte-timestamp-invalid')) {
    attrs.tabindex = '0'
    attrs.role = 'button'
    attrs['aria-label'] = `timestamp ${tc.fullMatch}`
  }
  return attrs
}

function getActivatableTimestamp(target: EventTarget | null): string | null {
  if (!(target instanceof HTMLElement)) return null
  if (
    !target.classList.contains('rte-timestamp-decoration') ||
    target.classList.contains('rte-timestamp-invalid')
  ) {
    return null
  }
  return target.getAttribute('data-timestamp')
}

export function createTimestampPlugin(
  onTimestampClick?: (timestamp: string) => void,
  getTrackDurationSeconds?: () => number | undefined,
  initialIsEditable: boolean = true,
) {
  let isEditable = initialIsEditable

  return new Plugin({
    key: timestampPluginKey,
    state: {
      init(_, state) {
        return buildDecorations(state.doc, getTrackDurationSeconds, isEditable)
      },
      apply(tr, oldDecorations, _oldState, newState) {
        const meta = tr.getMeta(timestampPluginKey)
        if (meta && meta.rebuild) {
          return buildDecorations(
            newState.doc,
            getTrackDurationSeconds,
            isEditable,
          )
        }
        if (!tr.docChanged) {
          return oldDecorations.map(tr.mapping, tr.doc)
        }
        return buildDecorationsIncremental(
          tr,
          oldDecorations,
          getTrackDurationSeconds,
          isEditable,
        )
      },
    },
    view(view) {
      const sync = () => {
        if (isEditable !== view.editable) {
          isEditable = view.editable
          view.dispatch(
            view.state.tr.setMeta(timestampPluginKey, { rebuild: true }),
          )
        }
      }
      sync()
      return { update: sync }
    },
    props: {
      decorations(state) {
        return timestampPluginKey.getState(state)
      },
      handleClick(view, _pos, event) {
        if (view.editable) {
          return false
        }
        const timestamp = getActivatableTimestamp(event.target)
        if (!timestamp) {
          return false
        }
        onTimestampClick?.(timestamp)
        return true
      },
      handleDOMEvents: {
        keydown(view, event) {
          if (view.editable || (event.key !== 'Enter' && event.key !== ' ')) {
            return false
          }

          const timestamp = getActivatableTimestamp(event.target)
          if (!timestamp) {
            return false
          }
          event.preventDefault()
          onTimestampClick?.(timestamp)
          return true
        },
      },
    },
  })
}

function buildDecorations(
  doc: ProseMirrorNode,
  getTrackDurationSeconds: (() => number | undefined) | undefined,
  isEditable: boolean,
): DecorationSet {
  const decorations: Decoration[] = []

  doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (!node.isText) {
      return
    }

    const text = node.text as string
    for (const tc of findTimestamps(text)) {
      const from = pos + tc.index
      const to = from + tc.fullMatch.length

      decorations.push(
        Decoration.inline(
          from,
          to,
          getDecorationAttrs(tc, getTrackDurationSeconds, isEditable),
        ),
      )
    }
  })

  return DecorationSet.create(doc, decorations)
}

function buildDecorationsIncremental(
  tr: Transaction,
  oldDecorations: DecorationSet,
  getTrackDurationSeconds: (() => number | undefined) | undefined,
  isEditable: boolean,
): DecorationSet {
  let changedFrom = Infinity
  let changedTo = -Infinity
  tr.mapping.maps.forEach((stepMap, i) => {
    stepMap.forEach((oldStart, oldEnd) => {
      const newStart = tr.mapping.slice(i).map(oldStart, -1)
      const newEnd = tr.mapping.slice(i).map(oldEnd, 1)
      changedFrom = Math.min(changedFrom, newStart)
      changedTo = Math.max(changedTo, newEnd)
    })
  })

  if (changedFrom > changedTo) {
    return oldDecorations.map(tr.mapping, tr.doc)
  }

  const $from = tr.doc.resolve(changedFrom)
  const $to = tr.doc.resolve(changedTo)
  const rangeFrom = $from.start($from.depth)
  const rangeTo = $to.end($to.depth)

  let mapped = oldDecorations.map(tr.mapping, tr.doc)
  mapped = mapped.remove(mapped.find(rangeFrom, rangeTo))

  const newDecorations: Decoration[] = []
  tr.doc.nodesBetween(
    rangeFrom,
    rangeTo,
    (node: ProseMirrorNode, pos: number) => {
      if (!node.isText) {
        return
      }
      const text = node.text as string
      for (const tc of findTimestamps(text)) {
        const from = pos + tc.index
        const to = from + tc.fullMatch.length
        newDecorations.push(
          Decoration.inline(
            from,
            to,
            getDecorationAttrs(tc, getTrackDurationSeconds, isEditable),
          ),
        )
      }
    },
  )

  return mapped.add(tr.doc, newDecorations)
}
